// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Chat settings > Swipe on chats. Whether phone Chats rows swipe at all,
// a live preview row that plays the swipe you are editing (and can be swiped
// by hand on a phone), icon tiles for a short and a long swipe each way, then
// the feel: the short-swipe tray, how far a swipe goes and haptics. Same look
// as Settings > Mail > Swipe actions.

import kitStyles from '@app/features/app/components/dialogs/shared/SettingsKit.module.css';
import {SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {
	CHAT_SWIPE_ACTION_LABEL,
	CHAT_SWIPE_ACTION_TONE,
	chatSwipeActionIcon,
} from '@app/features/channel/components/direct_message/ChatSwipeActionMeta';
import ChatsSwipePreferences, {
	CHAT_SWIPE_ACTIONS,
	CHAT_SWIPE_LONG_RATIO,
	type ChatSwipeAction,
	type ChatSwipeDirection,
	type ChatSwipeDistance,
	type ChatSwipeReach,
} from '@app/features/channel/state/ChatsSwipePreferences';
import {MailSwipeRow, type MailSwipeSpec} from '@app/features/konvo_mail/components/MailSwipeRow';
import styles from '@app/features/konvo_mail/components/MailSwipeSettings.module.css';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {ArrowsLeftRightIcon, HandTapIcon, KanbanIcon, VibrateIcon} from '@app/features/ui/icons/Icons';
import {Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

interface Slot {
	direction: ChatSwipeDirection;
	reach: ChatSwipeReach;
}

const SLOTS: ReadonlyArray<Slot> = [
	{direction: 'right', reach: 'short'},
	{direction: 'right', reach: 'long'},
	{direction: 'left', reach: 'short'},
	{direction: 'left', reach: 'long'},
];

const SLOT_LABEL: Record<`${ChatSwipeDirection}.${ChatSwipeReach}`, MessageDescriptor> = {
	'right.short': msg({message: 'Swipe right', comment: 'Chat settings > Swipe on chats: label above the tiles.'}),
	'right.long': msg({
		message: 'Long swipe right',
		comment: 'Chat settings > Swipe on chats: label above the tiles for a swipe past the second line.',
	}),
	'left.short': msg({message: 'Swipe left', comment: 'Chat settings > Swipe on chats: label above the tiles.'}),
	'left.long': msg({
		message: 'Long swipe left',
		comment: 'Chat settings > Swipe on chats: label above the tiles for a swipe past the second line.',
	}),
};
const PREVIEW_DESCRIPTOR = msg({
	message: 'Preview: {slot}',
	comment: 'Chat settings > Swipe on chats: caption above the live preview row. {slot} is e.g. "Swipe right".',
});
const PREVIEW_TRY_DESCRIPTOR = msg({
	message: 'Try it on the row',
	comment: 'Chat settings > Swipe on chats: the preview row can be swiped by hand on a phone.',
});
const SAMPLE_NAME = 'Lerato Mokoena';
const SAMPLE_TEXT_DESCRIPTOR = msg({
	message: 'Are we still on for the kiosk demo?',
	comment: 'Sample last message on the Chat settings swipe preview row. Any short chat message works.',
});

const slotKey = (slot: Slot) => `${slot.direction}.${slot.reach}` as const;

const toneOf = (action: ChatSwipeAction) =>
	action === 'none' ? 'var(--text-secondary)' : CHAT_SWIPE_ACTION_TONE[action];

/**
 * The row under test. Editing a short slot, it carries both lines and the demo pulls just past
 * the first; editing a long slot, the long action sits on the first line at the long distance so
 * the demo shows it.
 */
const PreviewRow = observer(({slot}: {slot: Slot}) => {
	const {i18n} = useLingui();
	const prefs = ChatsSwipePreferences;
	const long = slot.reach === 'long';
	const spec = (action: ChatSwipeAction): MailSwipeSpec | null =>
		action === 'none'
			? null
			: {
					key: action,
					label: i18n._(CHAT_SWIPE_ACTION_LABEL[action]),
					icon: chatSwipeActionIcon(action, 22),
					tone: 'archive',
					color: CHAT_SWIPE_ACTION_TONE[action],
					removes: false,
					run: () => undefined,
				};
	const side = (direction: ChatSwipeDirection) => ({
		first: spec(prefs.swipeAction(direction, slot.reach)),
		second: long ? null : spec(prefs.swipeAction(direction, 'long')),
	});
	const right = side('right');
	const left = side('left');
	const tray =
		!long && prefs.trayEnabled
			? [left.first, left.second].filter((entry): entry is MailSwipeSpec => entry !== null)
			: undefined;
	return (
		<div className={styles.preview}>
			<div className={styles.previewCaption}>
				<HandTapIcon size={14} />
				<span>{i18n._(PREVIEW_DESCRIPTOR, {slot: i18n._(SLOT_LABEL[slotKey(slot)])})}</span>
				{MobileLayout.enabled ? <span className={styles.previewTry}>{i18n._(PREVIEW_TRY_DESCRIPTOR)}</span> : null}
			</div>
			<MailSwipeRow
				key={slotKey(slot)}
				variant="card"
				className={styles.previewRow}
				leading={right.first}
				leadingLong={right.second}
				trailing={left.first}
				trailingLong={left.second}
				trailingTray={tray}
				longRatio={CHAT_SWIPE_LONG_RATIO}
				commitRatio={long ? CHAT_SWIPE_LONG_RATIO : prefs.distanceRatio}
				haptics={prefs.haptics}
				demo={slot.direction === 'right' ? 'leading' : 'trailing'}
				data-flx="user.chat-settings-tab.chats-swipe.preview"
			>
				<div className={styles.fakeRow}>
					<span className={styles.fakeAvatar}>LM</span>
					<span className={styles.fakeMain}>
						<span className={styles.fakeTop}>
							<strong>{SAMPLE_NAME}</strong>
							<span>09:12</span>
						</span>
						<span className={styles.fakePreview}>{i18n._(SAMPLE_TEXT_DESCRIPTOR)}</span>
					</span>
				</div>
			</MailSwipeRow>
		</div>
	);
});

const Tiles = observer(({slot, onPick}: {slot: Slot; onPick: () => void}) => {
	const {i18n} = useLingui();
	const current = ChatsSwipePreferences.swipeAction(slot.direction, slot.reach);
	const flx = `user.chat-settings-tab.chats-swipe.${slot.direction}-${slot.reach}`;
	return (
		<div className={styles.tiles} role="radiogroup" aria-label={i18n._(SLOT_LABEL[slotKey(slot)])} data-flx={flx}>
			{CHAT_SWIPE_ACTIONS.map((action) => {
				const on = action === current;
				return (
					<button
						key={action}
						type="button"
						role="radio"
						aria-checked={on}
						className={clsx(styles.tile, on && styles.tileOn)}
						style={{'--tile-tone': toneOf(action)} as React.CSSProperties}
						onClick={() => {
							ChatsSwipePreferences.setSwipeAction(slot.direction, slot.reach, action);
							onPick();
						}}
						data-flx={`${flx}.${action}`}
					>
						<span className={styles.tileWell}>{chatSwipeActionIcon(action, 22)}</span>
						<span className={styles.tileLabel}>{i18n._(CHAT_SWIPE_ACTION_LABEL[action])}</span>
					</button>
				);
			})}
		</div>
	);
});

export const ChatsSwipeSettings = observer(() => {
	const {t, i18n} = useLingui();
	const [previewing, setPreviewing] = useState<Slot>(SLOTS[0]);
	const prefs = ChatsSwipePreferences;
	const distanceOptions: ReadonlyArray<{value: ChatSwipeDistance; children: string}> = [
		{value: 'short', children: t`Short`},
		{value: 'medium', children: t`Medium`},
		{value: 'long', children: t`Long`},
	];
	return (
		<>
			<SettingsSection
				id="chats_swipe"
				title={<Trans>Swipe on chats</Trans>}
				description={
					<Trans>
						What a swipe does on a chat in the Chats list on your phone. Archive applies to WhatsApp chats and email,
						and email can't be muted. Saved on this device.
					</Trans>
				}
				data-flx="user.chat-settings-tab.chats-swipe"
			>
				<div className={kitStyles.rows}>
					<Switch
						label={t`Swipe chats`}
						description={t`Off: rows stay put. Long-press a chat for every action.`}
						value={prefs.enabled}
						onChange={prefs.setEnabled}
						icon={<ArrowsLeftRightIcon size={20} />}
						data-flx="user.chat-settings-tab.chats-swipe.enabled"
					/>
				</div>
				{prefs.enabled ? (
					<>
						<PreviewRow slot={previewing} />
						{SLOTS.map((slot) => (
							<div key={slotKey(slot)}>
								<div className={styles.groupLabel}>{i18n._(SLOT_LABEL[slotKey(slot)])}</div>
								<Tiles slot={slot} onPick={() => setPreviewing(slot)} />
							</div>
						))}
					</>
				) : null}
			</SettingsSection>
			{prefs.enabled ? (
				<SettingsSection
					id="chats_swipe_feel"
					title={<Trans>Swipe feel</Trans>}
					data-flx="user.chat-settings-tab.chats-swipe-feel"
				>
					<div className={kitStyles.rows}>
						<Switch
							label={t`Short-swipe tray`}
							description={t`A short pull left rests open on the left actions instead of springing back.`}
							value={prefs.trayEnabled}
							onChange={prefs.setTrayEnabled}
							icon={<KanbanIcon size={20} />}
							data-flx="user.chat-settings-tab.chats-swipe.tray"
						/>
						<SettingsRow
							icon={<ArrowsLeftRightIcon size={20} />}
							title={<Trans>Swipe distance</Trans>}
							description={<Trans>How far a swipe must go before letting go runs the action.</Trans>}
							control={() => (
								<Segmented
									compact
									value={prefs.distance}
									options={distanceOptions}
									onChange={prefs.setDistance}
									data-flx="user.chat-settings-tab.chats-swipe.distance"
								/>
							)}
						/>
						<Switch
							label={t`Haptic tick when armed`}
							description={t`A light tap as the swipe passes each line.`}
							value={prefs.haptics}
							onChange={prefs.setHaptics}
							icon={<VibrateIcon size={20} />}
							data-flx="user.chat-settings-tab.chats-swipe.haptics"
						/>
					</div>
				</SettingsSection>
			) : null}
		</>
	);
});
